import { useEffect, useState } from 'react';
import { Pagination } from '@quero-delivery/quero-components-web';
import { format } from '@querodelivery/qd-date-helper';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';
import * as Form from '@radix-ui/react-form';
import { Text, useTheme } from '@querodelivery/uni-react';
import { useDebounceValue } from 'usehooks-ts';
import { DEFAULT_LIMIT_REQUEST, MediaQueryMobile } from '@/Constants';
import type { GetPlacesProps } from '@/_features/places/api/get-places';
import { useGetPlaces } from '@/_features/places/api/get-places';
import {
  ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
  OPTIONS_CLOUD_ACTIVATION_REQUEST_STATUS,
  TRANSLATE_CLOUD_ACTIVATION_REQUEST_STATUS,
} from '@/_features/places/constants';
import { cnpjLabel, cpfLabel, objectNotNullV2 } from '@/Utils/FnUtils';
import type { Place } from '@/_types/places';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { setFilterCityOnPlacesList } from '@/_store/modules/filters/slice';
import { useCities } from '@/_hooks/use-cities';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { Table, useStackedTableLayout } from '@/_components/table';
import WhatsappNumber from '@/Components/WhatsappNumber/WhatsappNumber';
import type { PartnersListFormType } from '../../types';
import {
  PARTNERS_LIST_DEFAULT_STATUS_FILTERS,
  PARTNERS_LIST_MIN_NAME_LENGTH,
  PARTNERS_LIST_SCHEMA,
} from '../../constants';
import formSearchStyles from '../form-search/form-search.module.scss';
import styles from './partners-list.module.scss';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const limit = DEFAULT_LIMIT_REQUEST;
const PARTNERS_TABLE_GRID_COLUMNS = 'repeat(6, minmax(0, 1fr))';
const PARTNERS_NAME_SEARCH_DEBOUNCE_MS = 500;
const PLACE_DOCUMENT_EMPTY_LABEL = '- - -';

function getPlaceDocument(place: Place) {
  const cnpj = place.cnpj?.trim();
  const cpf = place.cpf?.trim();

  if (cnpj) {
    return {
      columnLabel: 'CNPJ',
      formatted: cnpjLabel(cnpj),
    };
  }

  if (cpf) {
    return {
      columnLabel: 'CPF',
      formatted: cpfLabel(cpf),
    };
  }

  return {
    columnLabel: 'Documento',
    formatted: PLACE_DOCUMENT_EMPTY_LABEL,
  };
}

export function PartnersList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isStackedTableLayout = useStackedTableLayout({
    customQuery: MediaQueryMobile,
  });
  const [page, setPage] = useState(1);
  const [params, setParams] = useState<GetPlacesProps>({
    page: 1,
    statusCloudActivationRequest: PARTNERS_LIST_DEFAULT_STATUS_FILTERS,
  });
  const { color } = useTheme();

  const filteredCityId = useAppSelector(
    state => state.filters.cityIdOnPlacesList,
  );

  const { CITIES_OPTIONS_WITH_ALL, loadingCities } = useCities();

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<PartnersListFormType>({
    resolver: yupResolver(PARTNERS_LIST_SCHEMA),
    defaultValues: {
      name: '',
      city: filteredCityId || '',
      statusCloudActivationRequest: undefined,
    },
  });

  const [debouncedPlaceName] = useDebounceValue(
    watch('name'),
    PARTNERS_NAME_SEARCH_DEBOUNCE_MS,
  );

  const { data: places, isLoading } = useGetPlaces({
    page,
    ...params,
  });

  const buildPlacesParams = (
    data: PartnersListFormType,
    currentPage: number,
  ): GetPlacesProps => {
    const hasStatusFilter = Object.values(
      ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
    ).includes(
      data.statusCloudActivationRequest as ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
    );

    const placeName =
      data.name && data.name.trim().length >= PARTNERS_LIST_MIN_NAME_LENGTH
        ? data.name.trim()
        : undefined;

    const statusCloudActivationRequest = hasStatusFilter
      ? [
          data.statusCloudActivationRequest as ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
        ]
      : placeName
      ? Object.values(ENUM_CLOUD_ACTIVATION_REQUEST_STATUS)
      : PARTNERS_LIST_DEFAULT_STATUS_FILTERS;

    return objectNotNullV2({
      placeName,
      cityId: data.city || undefined,
      statusCloudActivationRequest,
      page: currentPage,
    });
  };

  const submitForm = (data: PartnersListFormType) => {
    setTimeout(() => {
      setPage(1);
      setParams(buildPlacesParams(data, 1));
    }, 500);
  };

  useEffect(() => {
    const name = debouncedPlaceName ?? '';

    if (name.length === 1) {
      return;
    }

    setPage(1);
    setParams(buildPlacesParams({ ...getValues(), name }, 1));
  }, [debouncedPlaceName]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name !== 'name') {
        handleSubmit(submitForm)();
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  function getStatusLabel(status?: ENUM_CLOUD_ACTIVATION_REQUEST_STATUS) {
    if (!status) {
      return '- - -';
    }

    return TRANSLATE_CLOUD_ACTIVATION_REQUEST_STATUS[status];
  }

  function getStatusColorsByStatus(
    status?: ENUM_CLOUD_ACTIVATION_REQUEST_STATUS,
  ) {
    switch (status) {
      case 'PENDING':
        return {
          borderColor: color['color-warning-fill'],
          backgroundColor: color['color-warning-surface'],
        };
      case 'APPROVED':
        return {
          borderColor: color['color-success-fill'],
          backgroundColor: color['color-success-surface'],
        };
      case 'RESERVE_BASE':
        return {
          borderColor: color['color-danger-fill'],
          backgroundColor: color['color-danger-surface'],
        };
      default:
        return {
          border: 'none',
        };
    }
  }

  function renderStatusBadge(place: Place) {
    return (
      <div
        className={styles.tcStatus}
        style={getStatusColorsByStatus(place.cloudActivationRequest?.status)}
      >
        <Text variant="bodySm" tone="supporting">
          {getStatusLabel(place.cloudActivationRequest?.status)}
        </Text>
      </div>
    );
  }

  function handleNavigateToPlacePreRegistration(placeId: string) {
    navigate(`/precadastro/estabelecimentos/${placeId}`);
  }

  function renderPlaceRow(place: Place) {
    return (
      <Table.Row
        key={place._id}
        gridTemplateColumns={PARTNERS_TABLE_GRID_COLUMNS}
        onClick={() => handleNavigateToPlacePreRegistration(place._id)}
      >
        <Table.Cell className={styles.tcPlace}>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {place.nomeExibicao}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <WhatsappNumber number={place.celular} />
        </Table.Cell>
        <Table.Cell>
          <Text>{getPlaceDocument(place).formatted}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{`${place.cityName} - ${place.cityUf}`}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {place.cloudActivationRequest?.requestedAt
              ? format(
                  new Date(place.cloudActivationRequest?.requestedAt),
                  'dd/MM/yyyy HH:mm',
                )
              : '- - -'}
          </Text>
        </Table.Cell>
        <Table.Cell>{renderStatusBadge(place)}</Table.Cell>
      </Table.Row>
    );
  }

  function renderPlaceMobileCard(place: Place) {
    const placeDocument = getPlaceDocument(place);

    return (
      <Table.MobileCard
        key={place._id}
        onClick={() => handleNavigateToPlacePreRegistration(place._id)}
      >
        <Table.MobileItem label="Estabelecimento">
          <Text fontWeight="semibold" tone="supporting">
            {place.nomeExibicao}
          </Text>
        </Table.MobileItem>
        <Table.MobileItem label="Telefone">
          <WhatsappNumber number={place.celular} />
        </Table.MobileItem>
        <Table.MobileItem label={placeDocument.columnLabel}>
          {placeDocument.formatted}
        </Table.MobileItem>
        <Table.MobileItem label="Município">
          {`${place.cityName} - ${place.cityUf}`}
        </Table.MobileItem>
        <Table.MobileItem label="Solicitado em">
          {place.cloudActivationRequest?.requestedAt
            ? format(
                new Date(place.cloudActivationRequest?.requestedAt),
                'dd/MM/yyyy HH:mm',
              )
            : '- - -'}
        </Table.MobileItem>
        <Table.MobileItem label="Status">
          {renderStatusBadge(place)}
        </Table.MobileItem>
      </Table.MobileCard>
    );
  }

  function handleChangePage(newPage: number) {
    setPage(newPage);
    setParams(buildPlacesParams(getValues(), newPage));
  }

  return (
    <div>
      <Form.Root onSubmit={handleSubmit(submitForm)}>
        <div className={formSearchStyles.formContainer}>
          <div>
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  name="name"
                  value={value}
                  data-testid="input-place-name"
                  label="Buscar estabelecimento"
                  placeholder="Nome do estabelecimento"
                  onChange={onChange}
                  errorMessage={errors.name?.message}
                  error={!!errors.name?.message}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DropdownInput
                  data-testid="select-city"
                  clearable
                  search
                  value={value}
                  label="Município"
                  placeholder="Filtre o município"
                  options={CITIES_OPTIONS_WITH_ALL}
                  loading={loadingCities}
                  onChange={data => {
                    dispatch(setFilterCityOnPlacesList(data as string));
                    onChange(data as string);
                  }}
                  errorMessage={errors.city?.message}
                  error={!!errors.city?.message}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="statusCloudActivationRequest"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DropdownInput
                  data-testid="select-cloud-activation-status"
                  label="Filtros"
                  placeholder="Selecionar filtro"
                  clearable
                  options={OPTIONS_CLOUD_ACTIVATION_REQUEST_STATUS}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.statusCloudActivationRequest?.message}
                  error={!!errors.statusCloudActivationRequest?.message}
                />
              )}
            />
          </div>
        </div>
      </Form.Root>

      <div>
        {isLoading ? (
          <TableSkeleton columnCount={6} rowCount={5} />
        ) : places && places.data.length > 0 ? (
          <>
            <Text
              variant="headingMd"
              tone="supporting"
              className={styles.headerLabel}
            >
              {places.totalCount}{' '}
              {places.totalCount === 1 ? 'estabelecimento' : 'estabelecimentos'}
            </Text>
            {isStackedTableLayout ? (
              <Table.MobileList className={styles.partnersMobileList}>
                {places.data.map(place => renderPlaceMobileCard(place))}
              </Table.MobileList>
            ) : (
              <div className={styles.partnersTableScroll}>
                <Table className={styles.partnersTable}>
                  <Table.Header>
                    <Table.Row
                      gridTemplateColumns={PARTNERS_TABLE_GRID_COLUMNS}
                    >
                      <Table.HeaderCell>Estabelecimento</Table.HeaderCell>
                      <Table.HeaderCell>Telefone</Table.HeaderCell>
                      <Table.HeaderCell>CNPJ/CPF</Table.HeaderCell>
                      <Table.HeaderCell>Município</Table.HeaderCell>
                      <Table.HeaderCell>Solicitado em</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {places.data.map(place => renderPlaceRow(place))}
                  </Table.Body>
                </Table>
              </div>
            )}
            <div className={styles.pagination}>
              <Pagination
                size="sm"
                limitPerPage={limit}
                currentPage={page}
                totalCount={places.totalCount || 0}
                onPageChange={handleChangePage}
              />
            </div>
          </>
        ) : (
          <TableEmptyState
            title="Nenhum parceiro encotrado"
            description="Não encontramos parceiros com os filtros selecionados."
          />
        )}
      </div>
    </div>
  );
}
